// Integrations: overview, errors, spend and alerts (ff-api#1232).
// Reads /api/admin/v2/integrations: registry lifecycle status, the hourly
// vendor balance/quota snapshot, metered calls, recorded failures and alarms.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BellOff,
  CircleDollarSign,
  Loader2,
  Plug,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useAckAlarmMutation,
  useIntegrationAlarmsQuery,
  useIntegrationErrorsQuery,
  useIntegrationSpendQuery,
  useIntegrationsQuery,
} from '@/client/api/integrations/integrations.query';
import { ActivityState, Integration, IntegrationAlarm } from '@/client/types/integrations';
import { enableState } from './EnableControl';
import {
  AccessDenied,
  ErrorSampleRow,
  INTEGRATIONS_BASE,
  Pagination,
  PageShell,
  SeverityBadge,
  StatCard,
  StatusBadge,
  ACTIVITY_STATES,
  ACTIVITY_STYLE,
  ActivityBadge,
  UsageBar,
  ValidityCell,
  alarmName,
  fmtDate,
  SpendSourceBadge,
  WindowPicker,
  fmtErrorRate,
  fmtLatency,
  fmtMoney,
  fmtNum,
  fmtUsd,
  severityRank,
  usagePct,
} from './integrationsUi';

type Tab = 'overview' | 'errors' | 'spend' | 'alerts';

const GROUPINGS = {
  activity: (r: Integration) => r.activity?.state ?? 'untracked',
  category: (r: Integration) => r.category ?? 'uncategorised',
  repo: (r: Integration) => r.repo ?? 'unknown',
  status: (r: Integration) => r.status ?? 'unknown',
  health: (r: Integration) => String(r.computed?.health_severity ?? 'OK'),
  owner: (r: Integration) => r.owner ?? 'unassigned',
};
type GroupBy = '' | keyof typeof GROUPINGS;
// ponytail: grouping loads everything in one page; group server-side past 200 integrations.
const GROUPED_PER_PAGE = 200;

const IntegrationRow: React.FC<{ row: Integration }> = ({ row }) => {
  const { pct, label } = usagePct(row);
  const usage = row.usage;
  return (
    <tr className="border-b border-gray-50 last:border-0 hover:bg-gray-50/70">
      <td className="px-4 py-2">
        <Link
          to={`${INTEGRATIONS_BASE}/${encodeURIComponent(row.name)}`}
          className="font-medium text-gray-900 hover:underline"
        >
          {row.name}
        </Link>
        <p className="truncate text-xs text-gray-500">{row.provider ?? row.purpose ?? '—'}</p>
      </td>
      <td className="px-4 py-2 text-gray-600">{row.category ?? '—'}</td>
      <td className="px-4 py-2">
        <SeverityBadge severity={row.computed?.health_severity} />
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={row.status} />
        {enableState(row).effective ? null : (
          <span className="ml-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
            off
          </span>
        )}
      </td>
      <td className="max-w-[14rem] px-4 py-2">
        <ActivityBadge activity={row.activity} />
        <p className="mt-0.5 truncate text-xs text-gray-500" title={row.activity?.reason}>
          {row.activity?.reason}
        </p>
      </td>
      <td className="px-4 py-2">
        <UsageBar pct={pct} label={label} />
      </td>
      <td className="px-4 py-2">
        <ValidityCell validity={row.validity} />
      </td>
      <td
        className="px-4 py-2 text-right text-gray-700"
        title={
          usage?.vendor_calls == null && row.traffic?.calls
            ? 'Outbound calls recorded for this vendor. It does not report billed usage, so this is what we observed.'
            : undefined
        }
      >
        {/* Billed calls where the vendor meters them, otherwise what we saw on
            the wire — a vendor with real traffic should never read as "—". */}
        {usage?.vendor_calls != null ? (
          fmtNum(usage.vendor_calls)
        ) : row.traffic?.calls ? (
          <span className="text-gray-500">~{fmtNum(row.traffic.calls)}</span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-2 text-right text-gray-700">{fmtNum(usage?.cache_hits)}</td>
      <td className="px-4 py-2 text-right">
        <span className={(row.traffic?.error_rate ?? 0) > 0.05 ? 'text-red-600' : 'text-gray-700'}>
          {fmtErrorRate(row.traffic?.error_rate)}
        </span>
      </td>
      <td className="px-4 py-2 text-right text-gray-700">
        {usage?.est_cost ? fmtUsd(usage.est_cost) : '—'}
      </td>
      <td className="px-4 py-2 text-gray-500">
        {row.owner ? row.owner : <span className="text-amber-600">unassigned</span>}
      </td>
    </tr>
  );
};

const AlarmRow: React.FC<{
  alarm: IntegrationAlarm;
  canManage: boolean;
  onAck: (alarm: IntegrationAlarm) => void;
  acking: boolean;
}> = ({ alarm, canManage, onAck, acking }) => (
  <tr className="border-b border-gray-50 last:border-0">
    <td className="px-4 py-2">
      <SeverityBadge severity={alarm.severity} />
    </td>
    <td className="px-4 py-2">
      <Link
        to={`${INTEGRATIONS_BASE}/${encodeURIComponent(alarmName(alarm))}`}
        className="font-medium text-gray-900 hover:underline"
      >
        {alarmName(alarm)}
      </Link>
      <p className="text-xs text-gray-500">{alarm.condition ?? '—'}</p>
    </td>
    <td className="px-4 py-2 text-gray-700">{alarm.detail ?? '—'}</td>
    <td className="px-4 py-2 text-gray-500">{fmtDate(alarm.last_seen)}</td>
    <td className="px-4 py-2 text-gray-500">
      {alarm.ack ? `acked by ${alarm.ack.by ?? '—'}` : alarm.status ?? '—'}
      {alarm.flapping && <span className="ml-1 text-amber-600">(flapping)</span>}
    </td>
    <td className="px-4 py-2 text-right">
      {canManage && !alarm.ack && (
        <Button size="sm" variant="outline" disabled={acking} onClick={() => onAck(alarm)}>
          {acking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Ack'}
        </Button>
      )}
    </td>
  </tr>
);

const IntegrationsPage: React.FC<{ initialTab?: Tab }> = ({ initialTab = 'overview' }) => {
  useBreadcrumbs([
    { label: 'Integrations', href: INTEGRATIONS_BASE },
    ...(initialTab === 'overview'
      ? []
      : [{ label: initialTab.charAt(0).toUpperCase() + initialTab.slice(1) }]),
  ]);

  const viewPerm = useHasPermission('integration.view');
  const managePerm = useHasPermission('integration.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;

  const [tab, setTab] = useState<Tab>(initialTab);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [health, setHealth] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activity, setActivity] = useState('');
  const [only, setOnly] = useState<'' | 'erroring' | 'disabled' | 'unaudited'>('');
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [groupBy, setGroupBy] = useState<GroupBy>('category');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [alarmStatus, setAlarmStatus] = useState('active');
  const [ackingId, setAckingId] = useState<string | null>(null);

  // Filters are applied server-side, so they cover all 56 integrations rather
  // than whichever page happens to be loaded.
  const listQ = useIntegrationsQuery(
    {
      days,
      page: groupBy ? 1 : page,
      per_page: groupBy ? GROUPED_PER_PAGE : perPage,
      q: q.trim() || undefined,
      category: category || undefined,
      status: statusFilter || undefined,
      activity: activity || undefined,
      health: health || undefined,
      erroring: only === 'erroring' || undefined,
      disabled: only === 'disabled' || undefined,
      unaudited: only === 'unaudited' || undefined,
    },
    canView,
  );
  const errorsQ = useIntegrationErrorsQuery({ days, limit: 100 }, canView && tab === 'errors');
  const alarmsQ = useIntegrationAlarmsQuery(alarmStatus, canView && tab === 'alerts');
  const spendQ = useIntegrationSpendQuery(undefined, canView && tab === 'spend');
  const ackMutation = useAckAlarmMutation();

  const rows = listQ.data?.integrations ?? [];
  const summary = listQ.data?.summary;
  const unmatched = Object.entries(listQ.data?.unmatched_usage ?? {});

  const categories = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category).filter(Boolean) as string[])).sort(),
    [rows],
  );

  const visible = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          severityRank(a.computed?.health_severity) - severityRank(b.computed?.health_severity) ||
          a.name.localeCompare(b.name),
      ),
    [rows],
  );

  // Groups keep the worst-health-first order of their rows and of each other.
  const groups = useMemo(() => {
    if (!groupBy) return [];
    const out = new Map<string, Integration[]>();
    visible.forEach((r) => {
      const key = GROUPINGS[groupBy](r);
      out.set(key, [...(out.get(key) ?? []), r]);
    });
    // Activity reads worst-first; every other grouping alphabetically.
    const rank = (key: string) =>
      groupBy === 'activity' ? ACTIVITY_STATES.indexOf(key as ActivityState) : 0;
    return Array.from(out.entries()).sort(
      ([a], [b]) => rank(a) - rank(b) || a.localeCompare(b),
    );
  }, [visible, groupBy]);

  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // Any filter change invalidates the current page number.
  const onFilter = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  // Only vendors we actually called; worst error rate first.
  const called = useMemo(
    () =>
      rows
        .filter((r) => (r.traffic?.calls ?? 0) > 0)
        .sort((a, b) => (b.traffic?.error_rate ?? 0) - (a.traffic?.error_rate ?? 0)),
    [rows],
  );

  const handleAck = async (alarm: IntegrationAlarm) => {
    setAckingId(alarm._id);
    try {
      await ackMutation.mutateAsync({ id: alarm._id });
    } finally {
      setAckingId(null);
    }
  };

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Integrations" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Integrations" description="">
        <AccessDenied />
      </PageShell>
    );
  }

  const usage = summary?.usage;
  return (
    <PageShell
      title="Integrations"
      description="Every third-party vendor we call: lifecycle health, this month's usage against plan, and the alarms raised against them."
      actions={
        <div className="flex items-center gap-2">
        <WindowPicker days={days} onChange={(d) => { setDays(d); setPage(1); }} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => (tab === 'alerts' ? alarmsQ.refetch() : listQ.refetch())}
          disabled={listQ.isFetching || alarmsQ.isFetching}
        >
          <RefreshCw
            className={`mr-2 h-3.5 w-3.5 ${listQ.isFetching || alarmsQ.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Integrations"
          value={summary ? `${summary.live} live` : '—'}
          hint={summary ? `${summary.total} in registry` : undefined}
          icon={<Plug className="h-5 w-5 text-blue-600" />}
          color="bg-blue-50"
        />
        <StatCard
          label="Worst health"
          value={String(summary?.overall_health ?? '—')}
          hint={summary ? `${summary.open_alarms ?? 0} open alarms` : undefined}
          icon={<AlertTriangle className="h-5 w-5 text-orange-600" />}
          color="bg-orange-50"
        />
        <StatCard
          label="Calls this month"
          value={fmtNum(usage?.vendor_calls)}
          hint={
            usage?.cache_hit_pct != null
              ? `${usage.cache_hit_pct}% served from cache`
              : 'metered vendors only'
          }
          icon={<RefreshCw className="h-5 w-5 text-indigo-600" />}
          color="bg-indigo-50"
        />
        <StatCard
          label="Error rate"
          value={fmtErrorRate(summary?.traffic?.error_rate)}
          hint={`${fmtNum(summary?.traffic?.calls)} calls in ${summary?.traffic?.days ?? 7}d`}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          color="bg-red-50"
        />
        <StatCard
          label="Estimated spend"
          value={usage?.est_cost ? fmtUsd(usage.est_cost) : '—'}
          hint={`priced from ${usage?.metered_providers ?? 0} metered of ${summary?.total ?? 0} — see Spend for the rest`}
          icon={<CircleDollarSign className="h-5 w-5 text-green-600" />}
          color="bg-green-50"
        />
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {(['overview', 'errors', 'spend', 'alerts'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize ${
              tab === t
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
            {t === 'alerts' && (summary?.open_alarms ?? 0) > 0 && (
              <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                {summary?.open_alarms}
              </span>
            )}
          </button>
        ))}
      </div>

      {listQ.isError && tab === 'overview' && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          {getApiErrorMessage(listQ.error)}
        </div>
      )}

      {tab === 'overview' && (
        <>
          {/* How the estate is actually doing; a chip filters the table to it. */}
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by activity">
            {ACTIVITY_STATES.filter((st) => summary?.by_activity?.[st]).map((st) => (
              <button
                key={st}
                type="button"
                aria-pressed={activity === st}
                onClick={() => onFilter(setActivity)(activity === st ? '' : st)}
                className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${ACTIVITY_STYLE[st]} ${
                  activity === st ? 'ring-2' : ''
                } ${activity && activity !== st ? 'opacity-50' : ''}`}
              >
                {summary?.by_activity?.[st]} {st}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => onFilter(setQ)(e.target.value)}
                placeholder="Search name, provider, owner, notes"
                className="h-9 w-64 rounded-lg border border-gray-200 pl-8 pr-3 text-sm outline-none focus:border-blue-400"
              />
            </div>
            <select
              value={category}
              onChange={(e) => onFilter(setCategory)(e.target.value)}
              className="h-9 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={health}
              onChange={(e) => onFilter(setHealth)(e.target.value)}
              className="h-9 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
            >
              <option value="">Any health</option>
              {['CRITICAL', 'MAJOR', 'MINOR', 'WARNING'].map((h) => (
                <option key={h} value={h}>
                  {h} and worse
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => onFilter(setStatusFilter)(e.target.value)}
              className="h-9 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
            >
              <option value="">Any status</option>
              {['live', 'placeholder', 'planned', 'broken'].map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
            <select
              value={only}
              onChange={(e) => onFilter(setOnly)(e.target.value as typeof only)}
              className="h-9 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
            >
              <option value="">Everything</option>
              <option value="erroring">Only erroring</option>
              <option value="disabled">Only out of service</option>
              <option value="unaudited">Only unaudited</option>
            </select>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              aria-label="Group by"
              className="h-9 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
            >
              <option value="">No grouping</option>
              {Object.keys(GROUPINGS).map((g) => (
                <option key={g} value={g}>
                  Group by {g}
                </option>
              ))}
            </select>
            {summary && (
              <span className="ml-auto text-xs text-gray-500">
                <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />
                {listQ.data?.matched ?? 0} of {listQ.data?.total ?? summary.total} shown ·{' '}
                {summary.audited} of {summary.live} live audited ({summary.audited_pct}%)
              </span>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <th className="px-4 py-2 font-medium">Integration</th>
                    <th className="px-4 py-2 font-medium">Category</th>
                    <th className="px-4 py-2 font-medium">Health</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Activity</th>
                    <th className="px-4 py-2 font-medium">Usage</th>
                    <th className="px-4 py-2 font-medium">Valid until</th>
                    <th className="px-4 py-2 text-right font-medium">Calls</th>
                    <th className="px-4 py-2 text-right font-medium">Cache hits</th>
                    <th className="px-4 py-2 text-right font-medium">Err rate</th>
                    <th className="px-4 py-2 text-right font-medium">Est. cost</th>
                    <th className="px-4 py-2 font-medium">Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.isLoading && (
                    <tr>
                      <td colSpan={12} className="px-4 py-8 text-center text-gray-400">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                      </td>
                    </tr>
                  )}
                  {!listQ.isLoading && visible.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-4 py-8 text-center text-gray-400">
                        No integrations match this filter
                      </td>
                    </tr>
                  )}
                  {!groupBy && visible.map((row) => <IntegrationRow key={row.name} row={row} />)}
                  {groups.map(([key, members]) => (
                    <React.Fragment key={key}>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td colSpan={12} className="px-4 py-1.5">
                          <button
                            type="button"
                            onClick={() => toggleGroup(key)}
                            aria-expanded={!collapsed.has(key)}
                            className="flex w-full items-center gap-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                          >
                            <span className="w-3">{collapsed.has(key) ? '▸' : '▾'}</span>
                            {key}
                            <span className="font-normal normal-case tracking-normal text-gray-500">
                              {members.length} ·{' '}
                              {fmtNum(members.reduce((n, r) => n + (r.traffic?.calls ?? 0), 0))} calls
                            </span>
                            <SeverityBadge
                              severity={members[0].computed?.health_severity}
                            />
                          </button>
                        </td>
                      </tr>
                      {!collapsed.has(key) &&
                        members.map((row) => <IntegrationRow key={row.name} row={row} />)}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {!groupBy && (
            <Pagination
              page={listQ.data?.page ?? 1}
              pages={listQ.data?.pages ?? 1}
              total={listQ.data?.matched ?? 0}
              perPage={perPage}
              onPage={setPage}
              onPerPage={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
            )}
          </div>

          {unmatched.length > 0 && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
              <p className="font-medium">Metered but not in the registry</p>
              <p className="mt-1 text-amber-700">
                These providers report usage from ff-api-external but match no registry entry, so
                their lifecycle and quota go unwatched:{' '}
                {unmatched
                  .map(([name, u]) => `${name} (${fmtNum(u.vendor_calls ?? u.traffic?.calls)} calls)`)
                  .join(', ')}
              </p>
            </div>
          )}
        </>
      )}

      {tab === 'errors' && (
        <>
          <p className="text-sm text-gray-500">
            Outbound calls recorded by ff-api-external over the last{' '}
            {summary?.traffic?.days ?? 7} days. A vendor with no calls shows no error rate
            rather than a healthy zero.
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <th className="px-4 py-2 font-medium">Integration</th>
                    <th className="px-4 py-2 text-right font-medium">Calls</th>
                    <th className="px-4 py-2 text-right font-medium">Errors</th>
                    <th className="px-4 py-2 text-right font-medium">Err rate</th>
                    <th className="px-4 py-2 text-right font-medium">Auth</th>
                    <th className="px-4 py-2 text-right font-medium">429s</th>
                    <th className="px-4 py-2 text-right font-medium">Timeouts</th>
                    <th className="px-4 py-2 text-right font-medium">Avg lat.</th>
                    <th className="px-4 py-2 text-right font-medium">Trips</th>
                  </tr>
                </thead>
                <tbody>
                  {called.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                        {listQ.isLoading
                          ? 'Loading…'
                          : 'No outbound calls recorded yet. Traffic appears once ff-api-external ships the metering hook.'}
                      </td>
                    </tr>
                  )}
                  {called.map((row) => {
                    const t = row.traffic!;
                    return (
                      <tr key={row.name} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2">
                          <Link
                            to={`${INTEGRATIONS_BASE}/${encodeURIComponent(row.name)}`}
                            className="font-medium text-gray-900 hover:underline"
                          >
                            {row.name}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">{fmtNum(t.calls)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{fmtNum(t.errors)}</td>
                        <td className="px-4 py-2 text-right">
                          <span
                            className={
                              (t.error_rate ?? 0) > 0.05 ? 'font-medium text-red-600' : 'text-gray-700'
                            }
                          >
                            {fmtErrorRate(t.error_rate)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">
                          {t.auth_failed ? (
                            <span className="text-red-600">{fmtNum(t.auth_failed)}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">
                          {t.rate_limited ? fmtNum(t.rate_limited) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">
                          {t.err_timeout ? fmtNum(t.err_timeout) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">
                          {fmtLatency(t.avg_latency_ms)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">
                          {t.circuit_trips ? (
                            <span className="text-amber-600">{fmtNum(t.circuit_trips)}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="flex items-baseline justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-800">What actually failed</h3>
              <span className="text-xs text-gray-400">
                {errorsQ.data?.count ?? 0} recorded in the last {days === 1 ? '24h' : `${days}d`}
              </span>
            </div>
            <div className="px-4 py-2">
              {errorsQ.isLoading && (
                <p className="py-6 text-center text-gray-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </p>
              )}
              {!errorsQ.isLoading && (errorsQ.data?.errors.length ?? 0) === 0 && (
                <p className="py-6 text-center text-sm text-gray-400">
                  No failures recorded in this window.
                </p>
              )}
              <ul className="max-h-96 overflow-y-auto">
                {(errorsQ.data?.errors ?? []).map((sample, i) => (
                  <ErrorSampleRow key={`${sample.ts}-${i}`} sample={sample} showProvider />
                ))}
              </ul>
            </div>
            <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
              Credentials are stripped before these are stored, and the query string is dropped
              entirely — vendor keys live there.
            </p>
          </div>
        </>
      )}

      {tab === 'spend' && (
        <>
          <div className="flex flex-wrap items-center gap-4">
            {Object.entries(spendQ.data?.totals ?? {}).map(([currency, t]) => (
              <div key={currency} className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
                <p className="text-xs font-medium text-gray-500">{currency} this month</p>
                <p className="text-xl font-semibold text-gray-900">
                  {t.amount.toLocaleString()} {currency}
                </p>
                <p className="text-xs text-gray-400">
                  {t.projected.toLocaleString()} projected at month end
                </p>
              </div>
            ))}
            {spendQ.data && (
              <p className="text-xs text-gray-500">
                {spendQ.data.month}, {spendQ.data.month_elapsed_pct}% elapsed ·{' '}
                {spendQ.data.priced} of {spendQ.data.spend.length} integrations priced
              </p>
            )}
          </div>

          {(spendQ.data?.unknown_cost.length ?? 0) > 0 && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
              <p className="font-medium">We call these, but nobody has said what they cost</p>
              <p className="mt-1 text-amber-700">
                {spendQ.data?.unknown_cost.join(', ')} — set a cost on each detail page and it
                counts from then on.
              </p>
            </div>
          )}

          {spendQ.isError && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              {getApiErrorMessage(spendQ.error)}
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <th className="px-4 py-2 font-medium">Integration</th>
                    <th className="px-4 py-2 font-medium">Category</th>
                    <th className="px-4 py-2 text-right font-medium">Spend</th>
                    <th className="px-4 py-2 text-right font-medium">Projected</th>
                    <th className="px-4 py-2 text-right font-medium">Calls</th>
                    <th className="px-4 py-2 text-right font-medium">Cache saved</th>
                    <th className="px-4 py-2 font-medium">Where from</th>
                  </tr>
                </thead>
                <tbody>
                  {spendQ.isLoading && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                      </td>
                    </tr>
                  )}
                  {(spendQ.data?.spend ?? []).map((row) => (
                    <tr key={row.name} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-2">
                        <Link
                          to={`${INTEGRATIONS_BASE}/${encodeURIComponent(row.name)}`}
                          className="font-medium text-gray-900 hover:underline"
                        >
                          {row.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{row.category ?? '—'}</td>
                      <td className="px-4 py-2 text-right text-gray-900">{fmtMoney(row.amount, row.currency)}</td>
                      <td className="px-4 py-2 text-right text-gray-500">
                        {row.fixed ? '—' : fmtMoney(row.projected, row.currency)}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700">{fmtNum(row.calls)}</td>
                      <td className="px-4 py-2 text-right text-green-700">
                        {row.cache_saving ? fmtMoney(row.cache_saving, row.currency) : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <SpendSourceBadge source={row.source} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'alerts' && (
        <>
          <div className="flex items-center gap-2">
            <select
              value={alarmStatus}
              onChange={(e) => setAlarmStatus(e.target.value)}
              className="h-9 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
            >
              {['active', 'pending', 'acked', 'cleared', 'all'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {!canManage && (
              <span className="text-xs text-gray-500">
                Acknowledging alarms needs the integration.manage permission.
              </span>
            )}
          </div>

          {alarmsQ.isError && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              {getApiErrorMessage(alarmsQ.error)}
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <th className="px-4 py-2 font-medium">Severity</th>
                    <th className="px-4 py-2 font-medium">Integration</th>
                    <th className="px-4 py-2 font-medium">Detail</th>
                    <th className="px-4 py-2 font-medium">Last seen</th>
                    <th className="px-4 py-2 font-medium">State</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {alarmsQ.isLoading && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                      </td>
                    </tr>
                  )}
                  {!alarmsQ.isLoading && (alarmsQ.data?.alarms.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                        <BellOff className="mx-auto mb-2 h-5 w-5" />
                        No {alarmStatus === 'all' ? '' : alarmStatus} alarms
                      </td>
                    </tr>
                  )}
                  {(alarmsQ.data?.alarms ?? []).map((alarm) => (
                    <AlarmRow
                      key={alarm._id}
                      alarm={alarm}
                      canManage={canManage}
                      acking={ackingId === alarm._id}
                      onAck={handleAck}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default IntegrationsPage;
